/**
 * Copyright Mántum All Rights Reserved.
 */
import Row, { type Value } from './row';
import debug from 'debug';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { sort } from '@mantum/kanela';
import type { Columns, Detail } from '@mantum/kanela';

type PropTypes = {
    columns?: Columns;
    dataSource?: Record<string, unknown>[];
    disabled?: boolean;
    name?: string;
    onChange?: (name?: string, value?: unknown) => void;
    testid?: string;
};

const info = debug('kanela:datatable/simple');

export default forwardRef<Detail<unknown>, PropTypes>(
    function List(props, ref) {
        const rowRef = useRef<Record<string, Value>>({});
        const valuesRef = useRef<Record<string, unknown>>({});

        // prettier-ignore
        const {
            name,
            dataSource = []
        } = props;

        // prettier-ignore
        const columns = props.columns
            ?.filter((it) => it.visible !== false)
            ?.sort(sort.asc) || [];

        function subscribe(ref: Value) {
            if (ref) {
                rowRef.current[ref.name] = ref;
            }
        }

        function convert() {
            return Object.keys(valuesRef.current).map(
                (key) => valuesRef.current[key]
            );
        }

        useImperativeHandle(
            ref,
            () => {
                return {
                    name,
                    validable: false,
                    clear() {
                        const entries = Object.entries(rowRef.current);
                        for (const [, value] of entries) {
                            info('clear row');
                            value.clear();
                        }
                    },
                    validate() {
                        return true;
                    },
                    value() {
                        return convert();
                    },
                    focus() {}
                };
            },
            [name]
        );

        function onChange(name: string, value: Record<string, unknown>) {
            valuesRef.current[name] = value;
            const values = convert();

            if (props.onChange) {
                props.onChange(props.name, values);
            }
        }

        return (
            <table
                className='table-auto border w-auto'
                data-testid={props.testid}
            >
                <thead>
                    <tr>
                        {columns.map((it) => (
                            <th
                                key={it.name}
                                className='border h-10 px-2 min-w-36'
                            >
                                {it.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {dataSource.map((source, index) => (
                        <Row
                            columns={columns}
                            key={index}
                            name={`${name}-${index}`}
                            onChange={onChange}
                            ref={subscribe}
                            value={source}
                        />
                    ))}
                </tbody>
            </table>
        );
    }
);
